import { createSelector } from '@reduxjs/toolkit';
import { concat, isEmpty, isEqual, values, get, memoize } from 'lodash';
import markerStore from '@scout24/is24-cxp-map/src/features/styledMarker/services/markerStore.js';
import { NO_PRICE_RANGE, PALM_MAX } from '../utils/globals.js';
import { calculateDeltaInPercent, formatDE } from '../utils/NumbersHelper.js';
import { fullMapRoute, getActiveTab, getActiveView } from '../components/Navigation/Menu/menuConfig';
import geoHierarchyLevels from '../geoHierarchyLevels.js';
import titleExtractor from '../utils/titleExtractor';
import { sortListing } from '../components/ListingBar/listingSortUtils.js';
import realEstateTypes from '../realEstateTypes';
import userIntents from '../userIntents';
import { replaceDoubleName } from '../utils/districtNameUtils';

const sortSubHierarchy = memoize(
  ({ entries }) => entries.sort((first, second) => Intl.Collator().compare(first.name, second.name)),
  ({ geoHierarchy, entries }) => {
    if (entries.length === 0) {
      return 'empty';
    }
    return geoHierarchy[geoHierarchy.length - 1].link;
  },
);

export const getSelectedRealEstate = (state) => state.myProperty.selectedRealEstate;
export const getAllRealEstatesFromOma = (state) => state.myProperty.myPropertyAllRealEstates;
export const getRealEstateFromOma = (state) => state.myProperty.myPropertyRealEstate;
export const getRealEstateIDFromOma = (state) => state.myProperty.myPropertyRealEstate.realEstateId;
export const getHierarchyLevel = (state) => state.hierarchyLevel;
export const getGeoType = (state) => state.geoType;
export const getRealEstateType = (state) => state.realEstateType;
export const getIsInternalReferer = (state) => state.isInternalReferer;
export const getUserIntent = (state) => state.userIntent;
export const getAddress = (state) => {
  const address = state.address.data || {};
  // return address;
  return Object.assign(address, { district: replaceDoubleName(address.district) });
};
export const getAddressStatus = (state) => state.address.status;
export const getPriceHistory = (state) => state.priceHistory.data;
export const getPriceHistoryStatus = (state) => state.priceHistory.status;
export const getGeoHierarchy = (state) => {
  // return state.geoHierarchy.data;
  if (!state.geoHierarchy.data) {
    return state.geoHierarchy.data;
  }
  return state.geoHierarchy.data.map((element) => {
    if (element && element.level === 'district') {
      return Object.assign(element, { name: replaceDoubleName(element.name) });
    }
    return element;
  });
};
export const getGeoHierarchyStatus = (state) => state.geoHierarchy.status;
export const getRawListingHistory = (state) => state.listingHistory.data;
export const getRawMapListing = (state) => state.dynamicMap.listings;
export const getRawMapPreviewListing = (state) => state.dynamicMap.previewListings;
export const getRawListingBarListing = (state) => state.dynamicMap.listingBarListings;
export const getRawListingBarPreviewListing = (state) => state.dynamicMap.listingBarPreviewListings;
export const getListingHistoryStatus = (state) => state.listingHistory.status;
export const getStaticMaps = (state) => state.staticMaps.data;
export const getStaticMapsStatus = (state) => state.staticMaps.status;
export const getDemographicInfo = (state) => state.demographicInfo.data;
export const getDemographicInfoStatus = (state) => state.demographicInfo.status;
export const getAddressSearch = (state) => state.addressSearch;
export const getGoogleAddressSearch = (state) => state.googleAddressSearch;
export const getStreet = (state) => state.street;
export const getScrollY = (state) => state.scroll.scroll_y;
export const isBurgerMenuOpened = (state) => state.navigation.burgerMenuOpened;
export const isSidebarExpanded = (state) => state.navigation.sidebarExpanded;
export const getIsSidebarDesktopExpanded = (state) => state.navigation.sidebarDesktopExpanded;
export const getWindowWidth = (state) => state.window.width;
export const getPriceCheckSettings = (state) => state.priceCheckSettings || {};
export const getSelectedPointInTime = (state) => state.priceHistorySettings.selected || 0;
export const getShowStatsBox = (state) => state.priceHistorySettings.showStatsBox;
export const getStreetWithNumber = createSelector([getAddress], (address) =>
  address ? `${address.street} ${address.houseNumber}` : '',
);
export const getZipCode = createSelector([getAddress], (address) => address.zipcode);
export const getDistrictName = createSelector([getAddress], (address) => address.district);
export const getIsMapOn = (state) => state.pathname.startsWith(fullMapRoute);
export const getHash = (state) => state.pathname;
export const getPageLink = (state) => {
  try {
    return `${document.location.protocol}//${document.location.host}${document.location.pathname}${document.location.search}`;
    // eslint-disable-next-line no-empty
  } catch {}
  return state.pageLink;
};
export const getRleWidgetUrl = (state) => state.config.rleWidgetUrl || '';
export const getDemandCheckerWidgetUrl = (state) => state.config.demandCheckerWidgetUrl || '';
export const getRealEstateAPIUrl = (state) => state.config.realEstateAPIUrl;
export const getAllRealEstatesAPIUrl = (state) => state.config.myPropertyServiceAllRealEstatesAPIUrl;
export const getKauferPlusEntitlementAPIUrl = (state) => state.config.kauferPlusEntitlementAPIUrl;
export const getShapeServicesUrl = (state) => state.config.shapeServiceUrl;
export const getUpdateRealEstateAPIUrl = (state) => state.config.updateRealEstateUrl;
export const getSubHierarchyLinks = (state) => {
  const result = sortSubHierarchy({
    geoHierarchy: state.geoHierarchy.data || null,
    entries: state.subHierarchyInfo.data || [],
  });
  return result;
};
export const getSubHierarchyLinksStatus = (state) => state.subHierarchyInfo.status;
export const getPriceCheckRealEstateType = (state) => state.priceCheckSettings.realEstateType || state.realEstateType;
export const getConfig = (state) => state.config;
export const getRealtorLeadEngineEndpoint = (state) => state.config.realtorLeadEngineEndpoint || '';
export const isMobileNavigationMenuOpen = (state) => state.navigation.isMobileNavigationMenuOpen || '';
export const getIsEagerlyLoadingListingBarListings = (state) => state.dynamicMap.isEagerlyLoadingListingBarListings;
export const getInitialLoad = (state) => state.dynamicMap.isInitialLoad;
export const isSelectedRealEstateReady = (state) => !isEmpty(getSelectedRealEstate(state));
export const getKauferPlusEntitlement = (state) => get(state, ['auth', 'kauferPlusEntitlement'], null);
export const getDuplicateDistricts = (state) => state.duplicateDistricts;
export const getPriceTableData = (state) => state.priceTableData.data || null;
export const getSiblingsHierarchyInfo = (state) => state.siblingsHierarchyInfo.data || null;

export const getSelectedPriceValuationPdf = (state) => {
  const selectedRealEstate = getSelectedRealEstate(state);
  if (!selectedRealEstate) return null;

  const selectedPdf = selectedRealEstate.priceValuationPdf;
  if (!selectedPdf) return null;

  return selectedPdf;
};

export const getLocation = createSelector(
  [getHierarchyLevel, getGeoHierarchy, getAddress],
  (hierarchyLevel, geoHierarchy, address) => {
    const [, region, city, district, street] = geoHierarchy;

    if (hierarchyLevel === 'region') {
      return region.position;
    }

    if (hierarchyLevel === 'city') {
      return city.position;
    }

    if (hierarchyLevel === 'district') {
      return district.position;
    }

    if (hierarchyLevel === 'address') {
      return address.position;
    }

    if (hierarchyLevel === 'street') {
      return street.position;
    }

    return null;
  },
);

export const isUserIntentAndHierarchyForNewDashboard = createSelector(
  [getUserIntent, getHierarchyLevel],
  (userIntent, hierarchyLevel) =>
    (hierarchyLevel === geoHierarchyLevels.REGION ||
      hierarchyLevel === geoHierarchyLevels.CITY ||
      hierarchyLevel === geoHierarchyLevels.DISTRICT) &&
    (userIntent === userIntents.SELL || userIntent === userIntents.BUY),
);
const getPrices = createSelector(
  [getRealEstateType, getPriceHistory],
  (realEstateType, priceHistory) => priceHistory[realEstateType],
);

export const getLocationName = createSelector(
  [getHierarchyLevel, getGeoHierarchy, getStreetWithNumber],
  (hierarchyLevel, geoHierarchy, address) => {
    const [, region, city, district, street] = geoHierarchy;

    if (hierarchyLevel === 'region') {
      return region.name;
    }

    if (hierarchyLevel === 'city') {
      return city.name;
    }

    if (hierarchyLevel === 'district') {
      return district.name;
    }

    if (hierarchyLevel === 'street') {
      return street.name;
    }

    if (hierarchyLevel === 'address') {
      return address;
    }

    return '';
  },
);

export const getParentLocationName = createSelector(
  [getHierarchyLevel, getGeoHierarchy, getStreetWithNumber],
  (hierarchyLevel, geoHierarchy) => {
    const [, region, city, district, street] = geoHierarchy;

    if (hierarchyLevel === 'region') {
      return 'Deutschland';
    }

    if (hierarchyLevel === 'city') {
      return region.name;
    }

    if (hierarchyLevel === 'district') {
      return city.name;
    }

    if (hierarchyLevel === 'street') {
      return district.name;
    }

    if (hierarchyLevel === 'address') {
      return street.name;
    }

    return '';
  },
);

export const getLocationNameForAgentTouchpoint = createSelector(
  [getHierarchyLevel, getGeoHierarchy, getStreetWithNumber],
  (hierarchyLevel, geoHierarchy) => {
    const [, region, city, district] = geoHierarchy;

    if (hierarchyLevel === 'region') {
      return region.name;
    }

    if (hierarchyLevel === 'city') {
      return city.name;
    }

    return district ? district.name : '';
  },
);

export const getGeoId = createSelector([getHierarchyLevel, getGeoHierarchy], (hierarchyLevel, geoHierarchy) => {
  const [, region, city, district] = geoHierarchy;

  switch (hierarchyLevel) {
    case geoHierarchyLevels.REGION:
      return region.id;

    case geoHierarchyLevels.CITY:
      return city.id;

    case geoHierarchyLevels.DISTRICT:
      return district.id;

    case geoHierarchyLevels.STREET:
      return district.id;

    case geoHierarchyLevels.ADDRESS:
      return district.id;

    default:
      return '';
  }
});

export const getPricesByHierarchyLevel = (prices, hierarchyLevel) => {
  if (hierarchyLevel === 'region') {
    return { nativeData: prices.find((x) => x.type === 'REGION') };
  }

  if (hierarchyLevel === 'city') {
    return {
      nativeData: prices.find((x) => x.type === 'CITY'),
      parentData: prices.find((x) => x.type === 'REGION' && x.name !== 'Berlin' && x.name !== 'Hamburg'),
    };
  }

  if (hierarchyLevel === 'district') {
    return { nativeData: prices.find((x) => x.type === 'DISTRICT'), parentData: prices.find((x) => x.type === 'CITY') };
  }

  if (hierarchyLevel === 'address' || hierarchyLevel === 'street') {
    return { nativeData: prices.find((x) => x.type === 'NHOOD'), parentData: prices.find((x) => x.type === 'CITY') };
  }

  return {};
};

export const getPriceData = createSelector([getPrices, getHierarchyLevel], getPricesByHierarchyLevel);

export const getPriceRange = createSelector([getPriceData], ({ nativeData }) =>
  nativeData && nativeData.mostRecentData ? nativeData.mostRecentData.priceRange : null,
);
export const isPriceRangeAvailable = createSelector(
  [getPriceRange],
  (priceRange) => !!(priceRange && priceRange.lowerBound && priceRange.upperBound),
);
export const getPriceRangeText = createSelector([getPriceRange, isPriceRangeAvailable], (priceRange, isAvailable) =>
  isAvailable ? `${formatDE(priceRange.lowerBound, 0)} - ${formatDE(priceRange.upperBound, 0)} €/m²` : NO_PRICE_RANGE,
);
export const getMostRecentPriceRangeData = createSelector([getPriceData], ({ nativeData }) =>
  nativeData ? nativeData.mostRecentData : null,
);

export const getPricesPerQ = createSelector([getPriceData], ({ nativeData, parentData }) => ({
  nativeData: nativeData ? nativeData.pricesPerQ : [],
  parentData: parentData ? parentData.pricesPerQ : [],
}));

export const getPricesPerYear = createSelector([getPriceData], ({ nativeData, parentData }) => ({
  nativeDataMobile: nativeData ? nativeData.prices : [],
  parentDataMobile: parentData ? parentData.prices : [],
}));

export const getPercDiffPerQ = createSelector([getPriceData], ({ nativeData, parentData }) => {
  const currentData = nativeData || parentData;
  if (currentData && currentData.pricesPerQ) {
    const { pricesPerQ } = currentData;
    const mostRecentPrice = pricesPerQ[pricesPerQ.length - 1].price;
    const oldestPrice = pricesPerQ[0].price;
    return calculateDeltaInPercent(oldestPrice, mostRecentPrice);
  }
  return currentData ? currentData.percDiff : 0;
});

export const getPercDiffPerYear = createSelector([getPriceData], ({ nativeData, parentData }) => {
  const currentData = nativeData || parentData;
  if (currentData && currentData.prices) {
    const { prices } = currentData;
    const mostRecentPrice = prices[prices.length - 1].price;
    const oldestPrice = prices[0].price;
    return calculateDeltaInPercent(oldestPrice, mostRecentPrice);
  }
  return currentData ? currentData.percDiff : 0;
});

export const getParentLocation = createSelector(
  [getHierarchyLevel, getGeoHierarchy],
  (hierarchyLevel, geoHierarchy) => {
    const [, region, city, district] = geoHierarchy;

    if (hierarchyLevel === 'city') {
      return region.name;
    }

    if (hierarchyLevel === 'district') {
      return city.name;
    }

    if (hierarchyLevel === 'street') {
      return district.name;
    }

    if (hierarchyLevel === 'address') {
      return district.name;
    }
    return '';
  },
);

export const getCityOrRegionLocation = createSelector(
  [getHierarchyLevel, getGeoHierarchy],
  (hierarchyLevel, geoHierarchy) => {
    const [, region, city] = geoHierarchy;

    if (hierarchyLevel === 'city') {
      return region.name;
    }

    if (hierarchyLevel === 'district') {
      return city.name;
    }

    if (hierarchyLevel === 'street') {
      return city.name;
    }

    if (hierarchyLevel === 'address') {
      return city.name;
    }
    return '';
  },
);

export const mapRawListingsAtlasListings = ({
  houseRentListings,
  houseBuyListings,
  apartmentRentListings,
  apartmentBuyListings,
}) => ({
  HOUSE_RENT: houseRentListings,
  HOUSE_BUY: houseBuyListings,
  APARTMENT_RENT: apartmentRentListings,
  APARTMENT_BUY: apartmentBuyListings,
});
export const getListingHistory = createSelector([getRawListingHistory], mapRawListingsAtlasListings);
export const getMapListings = createSelector([getRawMapListing], mapRawListingsAtlasListings);
export const getListingBarListings = createSelector([getRawListingBarListing], mapRawListingsAtlasListings);
export const getListingBarPreviewListings = createSelector(
  [getRawListingBarPreviewListing],
  mapRawListingsAtlasListings,
);

export const getActiveListings = (realEstateType, listingHistory) =>
  listingHistory[realEstateType].filter((item) => item.status === 'Activated').length;

export const getNumberOfActiveListings = createSelector([getRealEstateType, getListingHistory], getActiveListings);

export const listingsForRealEstateType = createSelector(
  [getRealEstateType, getListingHistory],
  (realEstateType, listingHistory) => listingHistory[realEstateType],
);

export const getMapListingsForRealEstateType = createSelector(
  [getRealEstateType, getMapListings],
  (realEstateType, mapListings) => mapListings[realEstateType],
);

export const getListingBarListingsForRealEstateType = createSelector(
  [getRealEstateType, getListingBarListings],
  (realEstateType, listings) => listings[realEstateType],
);

export const getListingBarPreviewListingsForRealEstateType = createSelector(
  [getRealEstateType, getListingBarPreviewListings, getListingBarListings],
  (realEstateType, previewListings, listings) => {
    const result = [];
    for (let i = 0; i < previewListings[realEstateType].length; i += 1) {
      const listing = previewListings[realEstateType][i];
      const foundListing = listings[realEstateType].filter((element) => element.id === listing.id).length > 0;
      if (!foundListing) {
        result.push(listing);
        if (result.length === 3) {
          return result;
        }
      }
    }
    return result;
  },
);

export const getListingByListingId = (state, listingId) =>
  concat(
    ...[
      ...values(getRawMapListing(state)),
      ...values(getRawListingBarListing(state)),
      ...values(getRawListingHistory(state)),
    ],
  ).find((listing) => listing.id === listingId) || {};

export const getListingsAtSameMarker = (state, listingId) => {
  const currentListing = getListingByListingId(state, listingId);
  const listings = getMapListingsForRealEstateType(state);
  const sortedListings = sortListing(listings);
  return sortedListings.filter(
    (listing) =>
      markerStore.createPositionId(currentListing.position) === markerStore.createPositionId(listing.position),
  );
};

export const getAreListingBarListingsStale = createSelector(
  [getMapListingsForRealEstateType, getListingBarListingsForRealEstateType],
  (mapListings, listingBarListings) =>
    !isEqual(
      mapListings.reduce((obj, { id }) => ({ ...obj, [id]: id }), {}),
      listingBarListings.reduce((obj, { id }) => ({ ...obj, [id]: id }), {}),
    ),
);

export const getSuggestionQuery = createSelector([getAddressSearch], (addressSearch) => addressSearch.suggestionQuery);

export const getSuggestionResults = createSelector(
  [getAddressSearch],
  (addressSearch) => addressSearch.suggestionResults,
);

export const getSelectedSuggestionIndex = createSelector(
  [getAddressSearch],
  (addressSearch) => addressSearch.selectedSuggestionIndex,
);

export const getGoogleSuggestionQuery = createSelector(
  [getGoogleAddressSearch],
  (googleAddressSearch) => googleAddressSearch.suggestionQuery,
);

export const getGoogleSuggestionResults = createSelector(
  [getGoogleAddressSearch],
  (googleAddressSearch) => googleAddressSearch.suggestionResults,
);

export const getGoogleSelectedSuggestionIndex = createSelector(
  [getGoogleAddressSearch],
  (googleAddressSearch) => googleAddressSearch.selectedSuggestionIndex,
);

export const getGoogleAutoCompleteScriptInitialized = createSelector(
  [getGoogleAddressSearch],
  (googleAddressSearch) => googleAddressSearch.googleAutoCompleteScriptInitialized,
);

export const getGooglePublicApiKey = createSelector([getConfig], (config) => config.googleMapsPublicApiKey);

export const getHeaderVisibility = createSelector(
  [getWindowWidth, getScrollY, getIsMapOn],
  (width, scrollY, isMapOn) => {
    /* check for mobile logic */
    if (width <= PALM_MAX) {
      return isMapOn ? false : scrollY <= 0;
    }
    // used to hide/show top part of tablet
    return scrollY <= 68;
  },
);

export const getIsNavigationHeaderFolded = createSelector(
  [getScrollY, isBurgerMenuOpened, getWindowWidth, getIsMapOn],
  (scrollY, burgerMenuOpened, width, isMapOn) =>
    (scrollY > 0 && !burgerMenuOpened) || (isMapOn && width <= PALM_MAX && !burgerMenuOpened),
);

export const getPriceCheckInputs = createSelector(
  [getAddress, getPriceCheckSettings],
  (address, priceCheckSettings) => ({
    zipCode:
      priceCheckSettings.priceCheckInputs.zipCode === undefined
        ? address.zipcode
        : priceCheckSettings.priceCheckInputs.zipCode,

    street: priceCheckSettings.priceCheckInputs.zipCode === undefined ? address.street : undefined,

    houseNumber: priceCheckSettings.priceCheckInputs.zipCode === undefined ? address.houseNumber : undefined,

    area: priceCheckSettings.priceCheckInputs.area,
    livingArea: priceCheckSettings.priceCheckInputs.livingArea,
    rooms: priceCheckSettings.priceCheckInputs.rooms,
  }),
);

export const getTabName = createSelector([getHash], (hash) => getActiveTab(hash));

export const getViewName = createSelector([getHash], (hash) => getActiveView(hash));

export const getPageViewId = (state, { viewName = null } = {}) => {
  const hierarchyLevel = getHierarchyLevel(state);
  const realEstateType = getRealEstateType(state);
  const finalViewName = viewName || getViewName(state);
  const [commercializationType, propertyType] = (realEstateType || '').split('_');
  return `is24.de.propertybook.${hierarchyLevel || null}.${commercializationType || null}.${propertyType || null}.${
    finalViewName || null
  }`.toLowerCase();
};

export const getGeoData = createSelector([getGeoHierarchy, getAddress], (geoHierarchy, address) => ({
  geoHierarchy,
  address,
}));

export const getPropertyType = createSelector([getRealEstateType], (realEstateType) => realEstateType.split('_')[0]);
/*
 * dynamicMap selectors
 */

export const getZoomLevel = createSelector([getHierarchyLevel], (hierarchyLevel) => {
  switch (hierarchyLevel) {
    case geoHierarchyLevels.ADDRESS:
      return 16;
    case geoHierarchyLevels.STREET:
      return 16;
    case geoHierarchyLevels.DISTRICT:
      return 15;
    case geoHierarchyLevels.CITY:
      return 14;
    default:
      return 15;
  }
});

export const getQuarterBoundsFromListings = (listings) => {
  let quarterBounds = null;
  const positionsLat = [...new Set(listings.map(({ position }) => position.lat))];
  const positionsLng = [...new Set(listings.map(({ position }) => position.lng))];

  /* if there are not 2 or more unique lat / lng we can't use quarterBounds */
  if (positionsLat.length > 1 && positionsLng.length > 1) {
    const minLat = Math.min(...positionsLat);
    const maxLat = Math.max(...positionsLat);
    const minLng = Math.min(...positionsLng);
    const maxLng = Math.max(...positionsLng);

    quarterBounds = {
      southWest: {
        latitude: minLat,
        longitude: minLng,
      },
      northEast: {
        latitude: maxLat,
        longitude: maxLng,
      },
    };
  }

  return quarterBounds;
};

export const getQuarterBounds = createSelector([listingsForRealEstateType], (listings) =>
  getQuarterBoundsFromListings(listings),
);

export const getDemandInput = createSelector(
  [getLocation, getRealEstateType, getSelectedRealEstate],
  (globalLocation, globalRealEstateType, selectedRealEstate) => {
    const realEstate = selectedRealEstate;
    if (realEstate && realEstate.coordinates && realEstate.realEstateType) {
      return {
        coordinates: realEstate.coordinates,
        realEstateType: realEstate.realEstateType,
      };
    }
    return {
      coordinates: globalLocation,
      realEstateType: globalRealEstateType,
    };
  },
);

export const getPricesPerQuarterDashboard = createSelector(
  [getPriceHistory, getRealEstateType],
  (priceHistory, realEstateType) => {
    if (!(priceHistory && priceHistory[realEstateType] && priceHistory[realEstateType].length)) {
      return null;
    }
    const [{ pricesPerQ }] = priceHistory[realEstateType];
    return pricesPerQ;
  },
);

export const getPricesPerYearDashboard = createSelector(
  [getPriceHistory, getRealEstateType],
  (priceHistory, realEstateType) => {
    if (!(priceHistory && priceHistory[realEstateType] && priceHistory[realEstateType].length)) {
      return null;
    }
    const [{ prices }] = priceHistory[realEstateType];
    return prices;
  },
);

export const getPricesForHierarchyLevel = (prices, hierarchyLevel) =>
  prices.find((x) => x.type === hierarchyLevel.toUpperCase());

export const getPricesForDashboardChart = createSelector(
  [getPriceHistory, getRealEstateType, getHierarchyLevel],
  (priceHistory, realEstateType, hierarchyLevel) => {
    const userIntent = realEstateType.split('_')[1];
    if (!(priceHistory && priceHistory[realEstateType] && priceHistory[realEstateType].length)) {
      return null;
    }
    const apartmentPrices = getPricesForHierarchyLevel(priceHistory[`APARTMENT_${userIntent}`], hierarchyLevel);
    const housePrices = getPricesForHierarchyLevel(priceHistory[`HOUSE_${userIntent}`], hierarchyLevel);
    return {
      apartmentPrices,
      housePrices,
    };
  },
);

export const getPricesForPriceHistoryWidget = createSelector(
  [getPriceHistory, getHierarchyLevel],
  (priceHistory, hierarchyLevel) => {
    if (!priceHistory) {
      return null;
    }
    const prices = {};
    Object.keys(realEstateTypes).forEach((realEstateType) => {
      prices[realEstateType] = getPricesForHierarchyLevel(priceHistory[realEstateType], hierarchyLevel);
    });
    return prices;
  },
);

export const getAddressTitleAndSubtitle = createSelector(
  [getAddress, getGeoHierarchy, getHierarchyLevel, getDuplicateDistricts],
  (address, geoHierarchy, hierarchyLevel, duplicateDistricts) => {
    if (address === {} || geoHierarchy === {}) {
      return null;
    }
    return titleExtractor(hierarchyLevel, geoHierarchy, address, duplicateDistricts);
  },
);

export const getLatestQuarterPriceInformationData = createSelector([getPriceHistory], (priceHistory) => {
  const priceInformationData = {};
  const keys = Object.keys(priceHistory);
  keys.forEach((key) => {
    priceInformationData[key] = priceHistory[key][0].pricesPerQ[priceHistory[key][0].pricesPerQ.length - 1];
  });
  return priceInformationData;
});

export const getLatestYearPriceInformationData = createSelector([getPriceHistory], (priceHistory) => {
  const priceInformationData = {};
  const keys = Object.keys(priceHistory);
  keys.forEach((key) => {
    priceInformationData[key] = priceHistory[key][0].prices[priceHistory[key][0].prices.length - 1];
  });
  return priceInformationData;
});
